// Активност (04 §2) — what was said, and what the owner wrote down.
//
// Two halves, deliberately different:
//
//   · The outreach log is APPEND-ONLY. Every call is a row; a bad call is never edited into a good
//     one. That is what makes loss analytics honest, and it is why the form clears rather than
//     turning into an edit view.
//   · Notes are one field, saved automatically a second after typing stops. The owner writes them
//     while talking; a Save button is a thing to forget mid-sentence, and a lost note is a lost
//     call.
//
// Dictation uses the browser's own speech recognition where it exists. It is offered, never
// assumed: a browser without it simply shows the textarea, with no broken button next to it.

import { useEffect, useRef, useState } from 'react';
import {
  LOSS_REASONS,
  OUTCOMES,
  useLogOutreach,
  useOutreach,
  useSaveNotes,
  type OutreachEvent,
} from './useActivity.js';
import { Dictation } from './Dictation.js';

function LogEntry({ event }: { event: OutreachEvent }) {
  const when = new Date(event.happened_at).toLocaleString('bg-BG');
  const loss = LOSS_REASONS.find((reason) => reason.value === event.loss_reason);

  return (
    <li className="log__row">
      <div className="log__row-top">
        <span className="badge badge--info">{event.outcome}</span>
        {loss ? <span className="badge badge--danger">{loss.label}</span> : null}
        <span className="log__when">{when}</span>
      </div>
      <div className="log__what">
        {event.notes ? <span className="log__note">{event.notes}</span> : null}
        {event.next_follow_up ? (
          <span className="log__next">📅 Следващо: {event.next_follow_up}</span>
        ) : null}
      </div>
    </li>
  );
}

function OutreachForm({ leadId }: { leadId: number }) {
  const log = useLogOutreach(leadId);
  const [outcome, setOutcome] = useState<string>(OUTCOMES[0]);
  const [lossReason, setLossReason] = useState('');
  const [notes, setNotes] = useState('');
  const [nextFollowUp, setNextFollowUp] = useState('');

  const isLoss = outcome === 'разговор — без интерес';

  return (
    <form
      className="log__form"
      onSubmit={(event) => {
        event.preventDefault();
        log.mutate(
          {
            channel: 'call',
            outcome,
            lossReason: isLoss && lossReason !== '' ? lossReason : null,
            notes,
            nextFollowUp: nextFollowUp === '' ? null : nextFollowUp,
          },
          {
            onSuccess: () => {
              setNotes('');
              setNextFollowUp('');
              setLossReason('');
            },
          },
        );
      }}
    >
      <div className="log__line">
        <div className="field">
          <label className="field__label">Резултат от разговора</label>
          <select
            className="select"
            value={outcome}
            onChange={(event) => setOutcome(event.target.value)}
          >
            {OUTCOMES.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        {isLoss ? (
          <div className="field">
            <label className="field__label">Причина за отказ</label>
            <select
              className="select"
              value={lossReason}
              onChange={(event) => setLossReason(event.target.value)}
            >
              <option value="">причина…</option>
              {LOSS_REASONS.map((reason) => (
                <option key={reason.value} value={reason.value}>
                  {reason.label}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        <div className="field">
          <label className="field__label">Следващо обаждане</label>
          <input
            type="date"
            className="input"
            value={nextFollowUp}
            onChange={(event) => setNextFollowUp(event.target.value)}
            aria-label="Следващо обаждане"
          />
        </div>
      </div>

      <div className="log__line log__line--bottom">
        <input
          type="text"
          className="input"
          placeholder="Бележка по разговора…"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
        <Dictation onText={(text) => setNotes((current) => `${current} ${text}`.trim())} />
        <button type="submit" className="btn btn--primary btn--sm" disabled={log.isPending}>
          Запиши
        </button>
      </div>

      {log.isError ? <p className="log__err">{(log.error as Error).message}</p> : null}
    </form>
  );
}

function Notes({ leadId, initial }: { leadId: number; initial: string }) {
  const save = useSaveNotes();
  const [text, setText] = useState(initial);
  const [saved, setSaved] = useState(true);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef<{ leadId: number; notes: string } | null>(null);

  useEffect(() => {
    if (timer.current !== null) clearTimeout(timer.current);
    if (pending.current !== null) {
      save.mutate(pending.current);
      pending.current = null;
    }
    setText(initial);
    setSaved(true);
  }, [leadId]);

  const change = (value: string): void => {
    setText(value);
    setSaved(false);
    if (timer.current !== null) clearTimeout(timer.current);
    pending.current = { leadId, notes: value };
    timer.current = setTimeout(() => {
      const job = pending.current;
      pending.current = null;
      if (job !== null) save.mutate(job, { onSuccess: () => setSaved(true) });
    }, 1000);
  };

  return (
    <section className="notes">
      <div className="notes__head">
        <div>
          <h3>📝 Моите бележки</h3>
          <p className="notes__sub">Записват се автоматично докато пишете</p>
        </div>
        <div className="notes__actions">
          <span className={`badge ${saved ? 'badge--tier-a' : 'badge--warn'}`}>
            {saved ? '✓ записано' : 'записва се…'}
          </span>
          <Dictation onText={(spoken) => change(`${text} ${spoken}`.trim())} />
        </div>
      </div>
      <textarea
        className="textarea"
        value={text}
        onChange={(event) => change(event.target.value)}
        rows={7}
        placeholder="Каквото искате да си запомните за този обект…"
      />
    </section>
  );
}

export function ActivityTab({ leadId, notes }: { leadId: number; notes: string }) {
  const { data: events = [] } = useOutreach(leadId);

  return (
    <div className="activity">
      <section className="log">
        <div className="log__head">
          <div>
            <h3>📞 Обаждания</h3>
            <p className="log__sub">Добавете резултат от проведен разговор или насрочете следващ</p>
          </div>
          <span className="badge badge--neutral">{events.length} записа</span>
        </div>
        <OutreachForm leadId={leadId} />
        {events.length === 0 ? (
          <p className="log__empty">Още няма записани обаждания.</p>
        ) : (
          <ul className="log__list">
            {events.map((event) => (
              <LogEntry key={event.id} event={event} />
            ))}
          </ul>
        )}
      </section>

      <Notes leadId={leadId} initial={notes} />
    </div>
  );
}
